import axios from 'axios';

import api, { handleError } from '@/services/axiosInstance';
import type {
  DocumentationDocument,
  DocumentSearchResponse,
  DocumentStatus,
  ImportDocumentRequest,
  PickedIntegrationFile,
  UploadDocumentResponse,
} from '@/types/documents';

const DOCUMENTS_API_URL = {
  listMyDocuments: '/documents/myDocuments',
  listSharedDocuments: '/documents/sharedDocuments',
  single: '/documents/doc',
  upload: '/documents/upload_pdf',
  importFromIntegration: '/documents/import_from_integration',
  share: '/documents/share/id',
  search: '/documents/search',
};

const DOCUMENTS_BASE_URL = 'https://api.vh3connect.io/api:uCamsOiN';

// Local development only: the Xano import endpoint is built in parallel
// (vH3-Digital/paragon#54). Never set this flag on a deployed site.
const isImportMockEnabled =
  import.meta.env.DEV && import.meta.env.VITE_KB_IMPORT_MOCK === 'true';

const mockImport = async (
  body: ImportDocumentRequest
): Promise<UploadDocumentResponse> => {
  await new Promise(resolve => setTimeout(resolve, 2500));

  if (body.file_name.toLowerCase().includes('fail')) {
    throw new Error('The file has more than 150 pages.');
  }

  const summary = `Mock import of **${body.file_name}** from Google Drive.`;
  return {
    doc: {
      id: `mock-${body.file_id}`,
      name: body.file_name.replace(/\.pdf$/i, '') + '.pdf',
      file: { url: '' },
      status: 'processing',
      is_shared: body.share,
      category: '',
      summary,
      created_at: Date.now(),
      key_points: '',
    },
    summary,
  } as unknown as UploadDocumentResponse;
};

export const documentsApi = {
  getDocument: async (
    documentId: string
  ): Promise<DocumentationDocument[] | undefined> => {
    try {
      const { data } = await api.get<DocumentationDocument[]>(
        `${DOCUMENTS_API_URL.single}/${documentId}`,
        {
          baseURL: DOCUMENTS_BASE_URL,
        }
      );
      return data;
    } catch (error) {
      handleError(error, 'getDocument');
    }
  },

  /**
   * Refreshes a document and checks if its status has changed.
   * If the status has changed, it triggers callbacks and events to update the UI.
   *
   * @param documentId - The ID of the document to refresh
   * @param status - The current/expected status of the document
   * @param onStatusChange - Optional callback when status changes
   * @returns The updated document data or undefined if not found
   *
   * @example
   * ```typescript
   * // Basic usage
   * const updatedDoc = await documentsApi.refreshDocument({
   *   documentId: 'doc-123',
   *   status: 'processing'
   * });
   *
   * // With callback
   * await documentsApi.refreshDocument({
   *   documentId: 'doc-123',
   *   status: 'processing',
   *   onStatusChange: (doc) => {
   *     console.log(`Document ${doc.id} is now ${doc.status}`);
   *     // Update local state or trigger UI updates
   *   }
   * });
   * ```
   */
  refreshDocument: async ({
    documentId,
    status,
    onStatusChange,
  }: {
    documentId: string;
    status: DocumentStatus;
    onStatusChange?: (document: DocumentationDocument) => void;
  }): Promise<DocumentationDocument | undefined> => {
    try {
      const { data } = await api.get<DocumentationDocument>(
        `${DOCUMENTS_API_URL.single}/${documentId}`,
        {
          baseURL: DOCUMENTS_BASE_URL,
        }
      );

      if (!data) {
        console.warn(`Document ${documentId} not found`);
        return undefined;
      }

      // Check if the document status has changed
      if (data.status !== status) {
        console.log(
          `Document ${documentId} status changed from ${status} to ${data.status}`
        );

        // Trigger callback to update document lists and UI
        if (onStatusChange) {
          onStatusChange(data);
        }

        // Dispatch custom event for global listeners
        window.dispatchEvent(
          new CustomEvent('documentStatusChanged', {
            detail: {
              documentId,
              oldStatus: status,
              newStatus: data.status,
              document: data,
            },
          })
        );
      }

      return data;
    } catch (error) {
      handleError(error, 'refreshDocument');
    }
  },

  /**
   * Semantic Library search. The body carries no identity fields; Xano scopes
   * the search to the signed-in member. Callers treat a failure as "no
   * documents" (`handleError` only logs and rethrows, it never shows a banner).
   */
  search: async (
    query: string,
    limit: number,
    signal?: AbortSignal
  ): Promise<DocumentSearchResponse | undefined> => {
    try {
      const { data } = await api.post<DocumentSearchResponse>(
        DOCUMENTS_API_URL.search,
        { query, options: { limit } },
        { baseURL: DOCUMENTS_BASE_URL, signal }
      );
      return data;
    } catch (error) {
      handleError(error, 'searchDocuments');
    }
  },

  listMyDocuments: async ({
    pageParam = 1,
    pageSize = 24,
  }: {
    pageParam?: number;
    pageSize?: number;
  } = {}): Promise<
    | {
        documents: DocumentationDocument[];
        pagination?: {
          pageNumber: number;
          pageSize: number;
          pageItemCount: number;
        };
      }
    | undefined
  > => {
    try {
      const { data } = await api.get<DocumentationDocument[]>(
        DOCUMENTS_API_URL.listMyDocuments,
        {
          baseURL: DOCUMENTS_BASE_URL,
          params: {
            page: pageParam,
            per_page: pageSize,
          },
        }
      );
      const documents = data || [];
      return {
        documents,
        pagination: {
          pageNumber: pageParam,
          pageSize,
          pageItemCount: documents.length,
        },
      };
    } catch (error) {
      handleError(error, 'listMyDocuments');
    }
  },

  listSharedDocuments: async ({
    pageParam = 1,
    pageSize = 24,
  }: {
    pageParam?: number;
    pageSize?: number;
  } = {}): Promise<
    | {
        documents: DocumentationDocument[];
        pagination?: {
          pageNumber: number;
          pageSize: number;
          pageItemCount: number;
        };
      }
    | undefined
  > => {
    try {
      const { data } = await api.get<DocumentationDocument[]>(
        DOCUMENTS_API_URL.listSharedDocuments,
        {
          baseURL: DOCUMENTS_BASE_URL,
          params: {
            pageNumber: pageParam,
            pageSize,
          },
        }
      );
      const documents = data || [];
      return {
        documents,
        pagination: {
          pageNumber: pageParam,
          pageSize,
          pageItemCount: documents.length,
        },
      };
    } catch (error) {
      handleError(error, 'listSharedDocuments');
    }
  },

  upload: async (
    file: File,
    isShared: boolean
  ): Promise<UploadDocumentResponse | undefined> => {
    try {
      if (!file) throw new Error('No file provided');
      if (file.size > 100 * 1024 * 1024)
        throw new Error('File size must be less than 100MB');
      if (file.type !== 'application/pdf')
        throw new Error('Only PDF files are supported');

      const formData = new FormData();
      formData.append('pdf', file, file.name);
      formData.append('share', isShared ? 'true' : 'false');

      const { data } = await api.post<UploadDocumentResponse>(
        DOCUMENTS_API_URL.upload,
        formData,
        {
          baseURL: DOCUMENTS_BASE_URL,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300_000,
          onUploadProgress: progressEvent => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            console.log(`Upload progress: ${percent}%`);
          },
        }
      );

      return data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 413) {
          throw new Error('File size too large');
        } else if (error.response?.status === 415) {
          throw new Error('File type not supported');
        } else if (error.response?.status === 400) {
          const body = error.response.data as
            | { code?: unknown; message?: unknown }
            | undefined;
          if (
            body?.code === 'ERROR_CODE_INPUT_ERROR' &&
            typeof body.message === 'string'
          ) {
            throw new Error(body.message);
          }
          throw new Error('Invalid file or request');
        } else if (error.code === 'ECONNABORTED') {
          throw new Error('Upload timed out. Please try again.');
        }
      }

      handleError(error, 'uploadDocument');
    }
  },

  /**
   * Imports one file chosen in an integration's file picker. Xano downloads
   * it on the server as the signed-in member and runs the `upload_pdf`
   * pipeline, so the response is the same. No file content leaves the browser.
   */
  importFromIntegration: async (
    file: PickedIntegrationFile,
    isShared: boolean
  ): Promise<UploadDocumentResponse> => {
    const body: ImportDocumentRequest = {
      integration: file.integration,
      file_id: file.fileId,
      file_name: file.fileName,
      mime_type: file.mimeType,
      share: isShared,
    };

    if (isImportMockEnabled) return mockImport(body);

    try {
      const { data } = await api.post<UploadDocumentResponse>(
        DOCUMENTS_API_URL.importFromIntegration,
        body,
        {
          baseURL: DOCUMENTS_BASE_URL,
          timeout: 300_000,
        }
      );
      return data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message = (
          error.response?.data as { message?: unknown } | undefined
        )?.message;
        if (typeof message === 'string' && message) {
          throw new Error(message);
        }
        if (error.code === 'ECONNABORTED') {
          throw new Error('Import timed out. Please try again.');
        }
      }
      throw new Error('Could not import the file. Please try again.');
    }
  },

  getShareDocument: async ({
    documentId,
  }: {
    documentId: string;
  }): Promise<DocumentationDocument | undefined> => {
    const queryParams = `id=${documentId}`;

    try {
      const { data } = await api.get<DocumentationDocument>(
        `${DOCUMENTS_API_URL.share}?${queryParams}`,
        {
          baseURL: DOCUMENTS_BASE_URL,
        }
      );
      return data;
    } catch (error) {
      handleError(error, 'getShareDocument');
    }
  },

  updateShareDocument: async ({
    documentId,
    share,
  }: {
    documentId: string;
    share: boolean;
  }): Promise<DocumentationDocument | undefined> => {
    const queryParams = `id=${documentId}&share=${share.toString()}`;

    try {
      const { data } = await api.get<DocumentationDocument>(
        `${DOCUMENTS_API_URL.share}?${queryParams}`,
        {
          baseURL: DOCUMENTS_BASE_URL,
        }
      );
      return data;
    } catch (error) {
      handleError(error, 'updateShareDocument');
    }
  },
};
